import { Dictionary } from 'lodash';
import { useState, useEffect } from 'react';
import {
  AssignDriverRequest,
  Dispatch,
  Order,
  OrderProduct,
  OrderReturnStatus,
  OrderStatus,
  OrderType,
  SearchDispatchRequest,
} from '../../clients/quipli-service/order/order';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { DateTime, Interval } from 'luxon';
import { toast } from '../../util/toasts';
import { QuipliLink } from '../../components/link/quipli-link';
import { Typography } from '../../components/typography/typography';
import {
  AddressCell,
  AssignedUnitCell,
  CustomerNameCell,
  CustomerPhoneNumberCell,
  DateTimeCell,
  OrderNumberCell,
} from '../order/order-table-cells';
import { Location } from '../../clients/quipli-service/location/location';
import { SelectForm } from '../../components/select-form/select-form';
import { DataTable, DataTableColumn, DataTableRow } from '../../components/table/data-table';
import './dispatch.scss';
import { Admin } from '../../clients/quipli-service/admin/admin';
import { Modal } from '../../components/modal/modal';
import { ScheduledTimeForm } from './components/scheduled-time-form';
import { DispatchStatusTransitionButton } from '../dispatch-v2/dispatch-status-transition-button';

export const ScheduleTimeModal = ({
  setRefresh,
  isOpen,
  setIsOpen,
  dispatch,
  locations,
}: {
  setRefresh: (refresh: boolean) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  dispatch?: Dispatch;
  locations: Location[];
}) => {
  const onScheduledTimeUpdate = () => {
    toast('Successfully updated scheduled time for order');
    setRefresh(true);
    setIsOpen(false);
  };

  const onScheduledTimeError = () => {
    toast('Error updating the scheduled time', 'error');
    setIsOpen(false);
  };

  return (
    <>
      <Modal title={`Update scheduled time`} isOpen={isOpen} close={() => setIsOpen(false)} overflow="visible">
        {dispatch && (
          <ScheduledTimeForm
            orderId={dispatch.orderId}
            orderProductIds={{
              delivery: dispatch.products.delivery?.length ? dispatch.products.delivery.map((p) => p.id) : undefined,
              pickup: dispatch.products.pickup?.length ? dispatch.products.pickup.map((p) => p.id) : undefined,
            }}
            scheduledTime={dispatch.scheduledTime}
            timezone={locations.find((l) => l.id === dispatch.locationId)?.timezone}
            disabledIntervals={
              dispatch.products.delivery?.length
                ? [Interval.after(dispatch.startTime, { years: 10 })]
                : [Interval.before(dispatch.startTime, { years: 10 })]
            }
            update={() => onScheduledTimeUpdate()}
            error={() => onScheduledTimeError()}
          />
        )}
      </Modal>
    </>
  );
};

export const DispatchTable = ({
  request,
  drivers,
  locations,
  dispatchData,
  setRefresh,
}: {
  request: SearchDispatchRequest;
  drivers: Dictionary<Admin>;
  locations: Location[];
  dispatchData: Dispatch[];
  refresh: boolean;
  setRefresh: (refresh: boolean) => void;
}) => {
  const { getMultipleConfigurations, assignDriver } = useAPI();

  const [loading, setLoading] = useState<boolean>(false);
  const [unitTracking, setUnitTracking] = useState<string>();
  const [onlyOffrentPickup, setOnlyOffrentPickup] = useState<boolean>(false);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);

  const [selectedDispatch, setSelectedDispatch] = useState<Dispatch>();

  const [isScheduledTimeModalOpen, setIsScheduledTimeModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      const config = await getMultipleConfigurations(['CONF_UNIT_TRACKING_VIA', 'CONF_ONLY_OFFRENT_DISPATCH']);
      setUnitTracking(config.find((_) => _.variable == 'CONF_UNIT_TRACKING_VIA')?.value);
      setOnlyOffrentPickup(config.find((_) => _.variable == 'CONF_ONLY_OFFRENT_DISPATCH')?.value === '1');
    };
    init();
  }, []);

  const saveDriver = async (
    orderId: number,
    products: { delivery?: OrderProduct[]; pickup?: OrderProduct[] },
    driverId?: number,
  ) => {
    setLoading(true);
    try {
      const body: AssignDriverRequest = {
        orderId: orderId,
        orderProductIds: {
          delivery: products.delivery?.length ? products.delivery.map((p) => p.id) : undefined,
          pickup: products.pickup?.length ? products.pickup.map((p) => p.id) : undefined,
        },
        driverId,
      };

      await assignDriver(orderId, body);
      setRefresh(true);
      toast('Successfully updated driver for order');
    } catch (err) {
      console.error(err);
      toast('Error assigning the driver', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getClassForStatusLabel = (
    type: 'delivery' | 'pickup',
    onlyOffrentPickup: boolean,
    orderProductStatus?: string,
  ) => {
    switch (orderProductStatus) {
      case OrderReturnStatus.OnRent:
        return type === 'delivery'
          ? ['completed', 'Completed']
          : onlyOffrentPickup
          ? ['not-started', 'Not Started']
          : ['not-started', 'Ready for Pickup'];
      case OrderReturnStatus.OffRent:
        return type === 'delivery' ? ['completed', 'Completed'] : ['not-started', 'Ready for Pickup'];
      case OrderReturnStatus.Complete:
      case 'Overdue':
        return [orderProductStatus.toLowerCase(), orderProductStatus];
      default:
        return ['not-started', 'Not Started'];
    }
  };

  const buildDataRow = (
    row: Dispatch,
    trackingVia: 'serialNumber' | 'unitNumber',
    _locations: Location[],
    _drivers: Dictionary<Admin>,
    idx: number,
    _request: SearchDispatchRequest,
    onlyOffrentPickup: boolean,
  ) => {
    const returnStatus = getClassForStatusLabel(
      !row.products.delivery ? 'pickup' : 'delivery',
      onlyOffrentPickup,
      _request.date.startOf('day').toMillis() === DateTime.now().startOf('day').toMillis() &&
        row.startTime < _request.date.startOf('day')
        ? 'Overdue'
        : (row.products.delivery ?? row.products.pickup)![0].status,
    );
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {row.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Order #{' '}
            </Typography>
            <QuipliLink
              to={`orders/view/${row.orderId}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {row.orderNumber || 'N/A'}
            </QuipliLink>
            <Typography type="text-sm" weight="medium">
              {row.products.delivery?.length && row.products.pickup?.length
                ? 'Delivery and Pickup'
                : row.products.delivery?.length && row.products.delivery.every((p) => p.type === OrderType.Sale)
                ? 'Sale Delivery'
                : row.products.delivery?.length
                ? 'Delivery'
                : 'Pickup'}
            </Typography>
            <Typography type="text-sm" weight="regular">
              Driver: {row.driver?.adminFullName ?? 'Unassigned'}
            </Typography>
          </Typography>
        </>
      ),
      fields: {
        orderId: row.orderId,
        orderNumber: <OrderNumberCell order={{ id: row.orderId, orderNumber: row.orderNumber } as Order} />,
        customer: (
          <>
            <CustomerNameCell order={{ user: row.user } as Order} />{' '}
            {row.representative && <Typography type="text-xs">Rep: {row.representative.name}</Typography>}
          </>
        ),
        phone: (
          <>
            <CustomerPhoneNumberCell order={{ user: row.user } as Order} />
            {row.representative && row.representative.phone && (
              <Typography type="text-xs">
                Rep. Phone:
                <CustomerPhoneNumberCell order={{ user: { ...row.user, phone: row.representative.phone } } as Order} />
              </Typography>
            )}
          </>
        ),
        type: (
          <Typography type="text-sm" weight="medium">
            {row.products.delivery?.length && row.products.pickup?.length
              ? 'Delivery and Pickup'
              : row.products.delivery?.length && row.products.delivery.every((p) => p.type === OrderType.Sale)
              ? 'Sale Delivery'
              : row.products.delivery?.length
              ? 'Delivery'
              : 'Pickup'}
          </Typography>
        ),
        time: <DateTimeCell date={row.startTime} />,
        products: (
          <div>
            {row.products.pickup &&
              row.products.pickup.length &&
              row.products.delivery &&
              row.products.delivery.length && <div className="text-sm font-semibold">Deliver:</div>}
            {row.products.delivery && row.products.delivery.length && (
              <AssignedUnitCell
                order={
                  { type: OrderType.Rental, status: OrderStatus.Approved, products: row.products.delivery } as Order
                }
                trackingVia={trackingVia}
                locations={_locations}
                isOrderList={false}
              />
            )}

            {row.products.pickup &&
              row.products.pickup.length &&
              row.products.delivery &&
              row.products.delivery.length && <div className="text-sm font-semibold mt-3">Pickup:</div>}

            {row.products.pickup && row.products.pickup.length && (
              <AssignedUnitCell
                order={{ type: OrderType.Rental, status: OrderStatus.Approved, products: row.products.pickup } as Order}
                trackingVia={unitTracking === '1' ? 'serialNumber' : 'unitNumber'}
                locations={_locations}
                isOrderList={true}
              />
            )}
          </div>
        ),

        address: <AddressCell address={row.address} />,
        driver: (
          <div id={`select-driver-${row.orderId}-${row.startTime}`}>
            <SelectForm
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              className="min-w-[250px] w-full"
              isClearable={true}
              isOptionSelected={(o) => row.driver?.adminId === o.adminId}
              options={Object.values(_drivers).map((d) => ({ ...d }))}
              value={row.driver && _drivers[row.driver?.adminId]}
              getOptionLabel={(o) => o.adminFullName}
              onChange={(driver) => saveDriver(row.orderId, row.products, driver?.adminId)}
              isDisabled={
                loading ||
                (row.products.delivery?.length
                  ? [OrderReturnStatus.Complete, OrderReturnStatus.OnRent, OrderReturnStatus.OffRent].includes(
                      row.products.delivery[0].status ?? OrderReturnStatus.NotStarted,
                    )
                  : [OrderReturnStatus.Complete].includes(row.products.pickup![0].status ?? OrderReturnStatus.OnRent))
              }
            />
          </div>
        ),
        scheduledTime: (
          <div className="min-w-full min-h-full flex">
            {row.scheduledTime && row.scheduledTime.isValid ? (
              <div className="flex">
                <div className="w-1/2 mr-6">
                  <DateTimeCell date={row.scheduledTime} />
                </div>
                <div className="w-1/2 ml-1">
                  <button
                    className="button button-yellow button-primary mb-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDispatch(row);
                      setIsScheduledTimeModalOpen(true);
                      // displayScheduledTimeModalForRow(row, _locations);
                    }}
                  >
                    <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                      Update
                    </Typography>
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="button button-yellow button-primary mb-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDispatch(row);
                  setIsScheduledTimeModalOpen(true);
                  // displayScheduledTimeModalForRow(row, _locations);
                }}
              >
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                  Set Scheduled Time
                </Typography>
              </button>
            )}
          </div>
        ),
        status: (
          <div className={`w-fit px-[12px] py-[5px] rounded-2xl order-status-cell ${returnStatus[0]}`}>
            <Typography type="text-sm" weight="regular">
              {returnStatus[1]}
            </Typography>
          </div>
        ),
        actions: (
          <DispatchStatusTransitionButton
            orderDispatch={row}
            reload={() => {
              setRefresh(true);
            }}
          />
        ),
        rowNum: idx,
      },
    };
  };

  const colNames: DataTableColumn[] = [
    { label: 'Order Number', key: 'orderNumber' },
    { label: 'Customer', key: 'customer' },
    { label: 'Tel. No.', key: 'phone' },
    { label: 'Type', key: 'type' },
    { label: 'Time', key: 'time' },
    { label: 'Address', key: 'address' },
    { label: 'Driver', key: 'driver' },
    { label: 'Scheduled Time', key: 'scheduledTime' },
    { label: 'Products', key: 'products' },
    { label: 'Status', key: 'status' },
    { label: 'Actions', key: 'actions' },
  ];

  useEffect(() => {
    setTableData(
      dispatchData.map((item, idx) =>
        buildDataRow(
          item,
          unitTracking === '1' ? 'serialNumber' : 'unitNumber',
          locations,
          drivers,
          idx,
          request,
          onlyOffrentPickup,
        ),
      ),
    );
  }, [dispatchData, onlyOffrentPickup]);

  return (
    <>
      <DataTable columnNames={colNames} data={tableData} />
      <ScheduleTimeModal
        setRefresh={setRefresh}
        isOpen={isScheduledTimeModalOpen}
        setIsOpen={setIsScheduledTimeModalOpen}
        dispatch={selectedDispatch}
        locations={locations}
      />
    </>
  );
};
